import React, { useState, useEffect, useReducer } from 'react';
import ReactModal from 'react-modal';
import { ReactComponent as CloseModalIcon } from '../../assets/svg/CloseModalIcon.svg';
import { FaTimes } from 'react-icons/fa';
import { ReactComponent as ImageAddIcon } from '../../assets/svg/ImageAddIcon.svg';
import { ReactComponent as Save } from '../../assets/svg/Save.svg';
import '../../styles/modals/StudentInfoModal.css';
import Loading from '../Loading';
import { useUser } from '../../auth/UseUser';
import MDEditor from '@uiw/react-md-editor';
import { createEditSolution, getSolutionById, uploadFile, getLocales, upsertSolutionLocalizedAudio } from '../../server/server';
import { fileSystemUrl } from '../../server/connections';
import ListOfParagraphs from './ListOfParagraphs';
import { checkAllowedExtensions, notify } from '../../utils/util';

const INITIAL_PARAGRAPH = { text: '', order: 1, type: 'TEXT' };

function CreateEditSolutionModal({ modalIsOpen, closeModal, currentSolution, setCurrentSolution, topicId, reloadTopic }: { modalIsOpen: any; closeModal: any; currentSolution: any; setCurrentSolution: any; topicId: any; reloadTopic: any }) {
	const [loading, setLoading] = useState(false);
	const [title, setTitle] = useState('');
	const [audioTitle, setAudioTitle] = useState('');
	const [image, setImage] = useState<string | undefined>(undefined);
	const [imageId, setImageId] = useState('');
	const [selectedFile, setSelectedFile] = useState(undefined);
	const [imageIdError, setImageIdError] = useState(false);

	const [audio, setAudio] = useState<string | undefined>(undefined);
	const [audioId, setAudioId] = useState('');
	const [selectedAudioFile, setSelectedAudioFile] = useState(undefined);
	const [audioIdError, setAudioIdError] = useState(false);

	const [locales, setLocales] = useState<any[]>([]);
	const [localizedAudios, setLocalizedAudios] = useState<any[]>([]);
	const [pendingLocale, setPendingLocale] = useState<any>(null);
	const [localizedAudioUploading, setLocalizedAudioUploading] = useState(false);
	const [localizedAudioError, setLocalizedAudioError] = useState<string | null>(null);

	const [processing, setProcessing] = useState(false);
	const [paragraphTextError, setParagraphTextError] = useState(false);
	const [titleError, setTitleError] = useState(false);
	const [audioTitleError, setAudioTitleError] = useState(false);
	const [showErrorMessage, setShowErrorMessage] = useState(false);

	const [paragraphs, setParagraphs] = useState([INITIAL_PARAGRAPH]);

	useEffect(() => {
		if (modalIsOpen) {
			loadLocales();
			if (!!currentSolution) loadSolution(currentSolution.id);
		}
	}, [modalIsOpen]);

	const loadLocales = async () => {
		const resp = await getLocales();
		if (resp.status === 200) setLocales(resp.data);
	};

	const loadSolution = async (id: string) => {
		setLoading(true);
		const resp = await getSolutionById(id);
		if (resp.status == 200) {
			setCurrentSolution(resp.data);
			setLoading(false);
		} else {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!!currentSolution?.id) {
			setTitle(currentSolution.title);
			setAudioTitle(currentSolution.audioTitle);
			let list = currentSolution?.paragraphs?.map((item: any, index: number) => {
				delete item.id;
				if (!item.order) item.order = index + 1;
				if (!item.type) item.type = 'TEXT';
				return item;
			});
			setParagraphs(list);
			if (!!currentSolution?.image) {
				setImage(fileSystemUrl + '/' + currentSolution.image?.url);
				setImageId(currentSolution?.image?.id);
			}
			if (!!currentSolution?.audio) {
				setAudio(fileSystemUrl + '/' + currentSolution.audio?.url);
				setAudioId(currentSolution?.audio?.id);
			}
			if (Array.isArray(currentSolution?.localizedAudio)) {
				setLocalizedAudios(currentSolution.localizedAudio);
			}
		}
	}, [currentSolution]);

	const onSubmit = async () => {
		if (!validate()) return;
		let newSolution: any = {
			title: title,
			audioTitle: audioTitle,
			paragraphs: paragraphs,
			topicId: topicId,
		};
		if (imageId) newSolution.imageId = imageId;
		if (audioId) {
			newSolution.audioId = audioId;
		} else {
			newSolution.audioId = null;
			newSolution.audioTitle = '';
		}
		if (currentSolution?.id) {
			newSolution.id = currentSolution.id;
		}
		insertUpdateSolution(newSolution);
	};

	const insertUpdateSolution = async (newSolution: any) => {
		setProcessing(true);
		const resp = await createEditSolution(newSolution);
		if (resp.status === 201) {
			reloadTopic();
			closeModal();
			setProcessing(false);
			notify(!!newSolution?.id ? 'Solution is updated!' : 'Solution is created!');
		} else {
			notify('Error !');
			setProcessing(false);
		}
	};

	useEffect(() => {
		if (!selectedFile) {
			setImage(undefined);
			return;
		}
		console.log('set img');
		const objectUrl = URL.createObjectURL(selectedFile);
		setImage(objectUrl);
	}, [selectedFile]);

	useEffect(() => {
		if (!selectedAudioFile) {
			setAudio(undefined);
			return;
		}
		console.log('set img');
		const objectUrl = URL.createObjectURL(selectedAudioFile);
		setAudio(objectUrl);
	}, [selectedAudioFile]);

	const press_icon_upload = () => {
		document.getElementById('icon_upload_modal')?.click();
	};
	const press_upload_audio = () => {
		document.getElementById('audio_upload_modal')?.click();
	};
	const closeImage = () => {
		setImage(undefined);
		setSelectedFile(undefined);
		setImageId('');
	};
	const closeAudio = () => {
		setAudio(undefined);
		setSelectedAudioFile(undefined);
		setAudioId('');
	};
	const onImageChange = async (e: any) => {
		if (!e.target.files || e.target.files.length === 0) {
			setSelectedFile(undefined);
			return;
		}
		let file = e.target.files[0];
		if (file.size > 15728640) {
			alert('File is too big!');
			setSelectedFile(undefined);
			return;
		}
		if (!checkAllowedExtensions(file.name)) {
			alert('Type of file is not allowed!');
			return;
		}
		const resp = await uploadFile({ file: file });
		if (resp.status == 201) {
			const uploadedImage = resp.data;
			setImageId(uploadedImage.id);
		} else {
			return;
		}
		console.log('Selected file : ', file);
		setSelectedFile(file);
	};
	const onAudioChange = async (e: any) => {
		if (!e.target.files || e.target.files.length === 0) {
			setSelectedAudioFile(undefined);
			return;
		}
		let file = e.target.files[0];
		if (file.size > 15728640) {
			alert('File is too big!');
			setSelectedAudioFile(undefined);
			return;
		}
		const resp = await uploadFile({ file: file });
		if (resp.status == 201) {
			const uploadedImage = resp.data;
			setAudioId(uploadedImage.id);
		} else {
			return;
		}
		console.log('Selected audio file : ', file);
		setSelectedAudioFile(file);
	};
	const onLocalizedAudioChange = async (e: any) => {
		if (!e.target.files || e.target.files.length === 0) return;
		if (!pendingLocale) {
			setLocalizedAudioError('Select a locale first.');
			return;
		}
		const file = e.target.files[0];
		if (file.size > 15728640) {
			alert('File is too big!');
			return;
		}
		setLocalizedAudioUploading(true);
		setLocalizedAudioError(null);

		const uploadResp = await uploadFile({ file });
		if (uploadResp.status !== 201) {
			setLocalizedAudioUploading(false);
			setLocalizedAudioError('File upload failed. Please try again.');
			return;
		}
		const { id: mediaId, url: mediaUrl } = uploadResp.data;

		const upsertResp = await upsertSolutionLocalizedAudio({
			solutionId: currentSolution.id,
			localeId: pendingLocale.id,
			mediaId,
		});
		if (upsertResp.status !== 201) {
			setLocalizedAudioUploading(false);
			setLocalizedAudioError('Failed to save localized audio. Please try again.');
			return;
		}

		setLocalizedAudios((prev) => {
			const newEntry = { locale: { ...pendingLocale }, audio: { id: mediaId, url: mediaUrl } };
			const exists = prev.find((entry) => entry.locale.id === pendingLocale.id);
			return exists ? prev.map((entry) => (entry.locale.id === pendingLocale.id ? newEntry : entry)) : [...prev, newEntry];
		});
		setPendingLocale(null);
		setLocalizedAudioUploading(false);
		notify('Localized audio saved!');
	};

	const removeLocalizedAudio = (localeId: string) => {
		setLocalizedAudios((prev) => prev.filter((entry) => entry.locale.id !== localeId));
	};

	const resetInputValue = (e: any) => {
		e.target.value = null;
	};
	const resetAudioInputValue = (e: any) => {
		e.target.value = null;
	};

	const validate = () => {
		let valid = true;
		paragraphs.forEach((element: any) => {
			if (element.text === '') {
				setParagraphTextError(true);
				valid = false;
				return;
			}
		});
		if (title === '') {
			setTitleError(true);
			valid = false;
		}

		if (audioId && audioTitle === '') {
			setAudioTitleError(true);
			valid = false;
		}
		// if (!imageId) {
		// 	setImageIdError(true);
		// 	valid = false;
		// }
		// if (!audioId) {
		// 	setAudioIdError(true);
		// 	valid = false;
		// }

		return valid;
	};

	const resetModal = () => {
		setTitle('');
		setAudioTitle('');
		setImage(undefined);
		setImageId('');
		setAudio(undefined);
		setAudioId('');
		setParagraphs([INITIAL_PARAGRAPH]);
		setImageIdError(false);
		setAudioIdError(false);
		setParagraphTextError(false);
		setTitleError(false);
		setAudioTitleError(false);
		setLocales([]);
		setLocalizedAudios([]);
		setPendingLocale(null);
		setLocalizedAudioUploading(false);
		setLocalizedAudioError(null);
	};
	return (
		<ReactModal
			isOpen={modalIsOpen}
			onAfterOpen={() => {}}
			onAfterClose={() => resetModal()}
			onRequestClose={closeModal}
			style={{
				overlay: {
					zIndex: 0,
					background: '#00000045',
				},
				content: {
					padding: 0,
					paddingTop: 30,
					paddingBottom: 30,
					opacity: 1,
					width: 900,
					margin: 'auto',
					backgroundColor: 'transparent',
					maxHeight: '100vh',
					border: 'none',
					boxShadow: 'none',
					overflow: 'auto',
				},
			}}
			className={'scrollbar-display-none'}
		>
			<div className='student-info-modal  '>
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<div className='close-modal-icon ' onClick={() => closeModal()}>
						<CloseModalIcon />
					</div>
				</div>

				<div className='bg-white rounded-xl mx-3 flex-1 flex flex-col gap-5 px-[40px] py-[30px] '>
					<div className='flex flex-row items-center justify-between'>
						<div className='flex-1'>
							<p className='text-primaryViolet font-semibold text-3xl text-left'>{!currentSolution?.id ? 'Create solution' : 'Edit solution'}</p>
						</div>
					</div>
					<div>
						<div className='flex flex-row justify-between gap-5'>
							<div className='flex-1'>
								<p className='text-primaryViolet text-[16px] leading-5 font-semibold text-left'>Title</p>
								<div className={`border-b   p-2 mt-2 flex flex-row items-center ${titleError && title === '' ? 'border-b-redError' : 'border-b-deselectPurple'}`}>
									<input className={`w-full text-[16px] leading-5 font-semibold text-primaryViolet ${titleError && title === '' ? 'placeholder:text-redError' : ''}`} placeholder={`Type solution title`} value={title} onChange={(e) => setTitle(e.target.value)} style={{ backgroundColor: 'transparent' }} />
									<p className='text-redError text-[16px] leading-5 font-medium text-nowrap'>{titleError && title === '' ? '*This field is required' : ''}</p>
								</div>
							</div>
							<div className='flex flex-col justify-center items-center'>
								<p className='text-primaryViolet text-[16px] leading-5 font-semibold mb-2'>Solution image</p>
								{!image && (
									<div className='flex flex-col items-center justify-center bg-containerGray rounded-xl py-8 w-[334px]'>
										<ImageAddIcon style={{ width: 42, height: 42, color: '#8484b1' }} />
										<p className='text-mudPurple font-medium text-[16px] leading-5 text-center w-[160px]'>The maximum image size for download is 1400px x 1400px</p>
										<button
											className='text-primaryViolet rounded-full bg-lightBG py-1 font-semibold text-[18px] leading-6 px-5 mt-3'
											onClick={(e) => {
												e.preventDefault();
												press_icon_upload();
											}}
										>
											Add Photo
										</button>
									</div>
								)}
								{image && (
									<div className='flex relative max-h-[244px] flex-col items-center justify-center bg-greyBackground rounded-xl pt-12 pb-4 w-[334px]'>
										<div onClick={() => closeImage()} className='bg-white opacity-40 p-2 rounded-full cursor-pointer border border-greyLight absolute top-[8px] right-[8px] hover:border-greyMedium'>
											<FaTimes style={{ width: 20, height: 20, stroke: 'white', display: 'block' }} />
										</div>

										<img style={{ objectFit: 'contain', width: '100%', minHeight: 170 }} src={image} alt='' />
									</div>
								)}
								<input title='Add Video/Photo' style={{ display: 'none' }} accept='image/*' type='file' id='icon_upload_modal' onChange={onImageChange} onClick={resetInputValue} />
								<p className='text-redError font-medium text-[14px] leading-5 h-5 '>{imageIdError && imageId === '' ? '* Image is required' : ''}</p>
							</div>
						</div>
						<div className='flex flex-row justify-between gap-5 mt-2'>
							<div className='flex-1'>
								<p className='text-primaryViolet text-[16px] leading-5 font-semibold text-left'>Audio file title</p>
								<div className={`border-b   p-2 mt-2 flex flex-row items-center ${audioTitleError && audioTitle === '' ? 'border-b-redError' : 'border-b-deselectPurple'}`}>
									<input className={`w-full text-[16px] leading-5 font-semibold text-primaryViolet ${audioTitleError && audioTitle === '' ? 'placeholder:text-redError' : ''}`} placeholder={`Audio file title`} value={audioTitle} onChange={(e) => setAudioTitle(e.target.value)} style={{ backgroundColor: 'transparent' }} />
									<p className='text-redError text-[16px] leading-5 font-medium text-nowrap'>{audioTitleError && audioTitle === '' ? '*This field is required' : ''}</p>
								</div>
							</div>
							<div className='flex flex-col justify-center items-center'>
								<p className='text-primaryViolet text-[16px] leading-5 font-semibold mb-2'>Audio file</p>
								{!audio && (
									<div className='flex flex-col items-center justify-center bg-containerGray rounded-xl py-8 w-[334px]'>
										<ImageAddIcon style={{ width: 42, height: 42, color: '#8484b1' }} />
										<p className='text-mudPurple font-medium text-[16px] leading-5 text-center w-[160px]'>The maximum audio size for download is 6MB</p>
										<button
											className='text-primaryViolet rounded-full bg-lightBG py-1 font-semibold text-[18px] leading-6 px-5 mt-3'
											onClick={(e) => {
												e.preventDefault();
												press_upload_audio();
											}}
										>
											Add Audio
										</button>
									</div>
								)}
								{audio && (
									<div className='flex relative max-h-[244px] flex-col items-center justify-center bg-containerGray rounded-xl py-8 w-[334px]'>
										<div onClick={() => closeAudio()} className='absolute top-0 right-0 p-[10px] rounded-[20px] bg-white cursor-pointer opacity-40'>
											<FaTimes style={{ width: 20, height: 20, stroke: 'white', display: 'block' }} />
										</div>

										<audio controls>
											<source src={audio} type='audio/mp3' />
											Vaš pretraživač ne podržava reprodukciju zvuka.
										</audio>
									</div>
								)}
								<input title='Add Video/Photo' style={{ display: 'none' }} accept='audio/*' type='file' id='audio_upload_modal' onChange={onAudioChange} onClick={resetAudioInputValue} />
								<p className='text-redError font-medium text-[14px] leading-5 h-5 '>{audioIdError && audioId === '' ? '* Audio is required' : ''}</p>
							</div>
						</div>
					{/* Localized Audio Section */}
					<div className='mt-4'>
						<p className='text-primaryViolet text-[16px] leading-5 font-semibold text-left mb-3'>Localized Audio</p>
						{!currentSolution?.id ? (
							<p className='text-mudPurple font-medium text-[14px] leading-5'>Save the solution first before adding localized audio overrides.</p>
						) : (
							<div className='flex flex-col gap-3'>
								{localizedAudios.map((entry: any) => (
									<div key={entry.locale.id} className='flex flex-row items-center justify-between gap-4 rounded-xl bg-containerGray px-4 py-3'>
										<div className='flex flex-row items-center gap-3 flex-1'>
											<span className='text-primaryViolet font-semibold text-[14px] min-w-[40px] uppercase'>{entry.locale.code}</span>
											<span className='text-mudPurple text-[13px] truncate'>{entry.locale.name}</span>
										</div>
										<audio controls className='h-[36px] flex-1 max-w-[220px]'>
											<source src={fileSystemUrl + '/' + entry.audio.url} type='audio/mp3' />
										</audio>
										<button
											type='button'
											className='text-primaryViolet text-[13px] font-semibold underline hover:opacity-70 transition'
											onClick={() => {
												setPendingLocale({ id: entry.locale.id, name: entry.locale.name, code: entry.locale.code });
												document.getElementById('localized_audio_upload_modal')?.click();
											}}
										>
											Replace
										</button>
										<button
											type='button'
											className='text-redError text-[13px] font-semibold hover:underline transition'
											onClick={() => removeLocalizedAudio(entry.locale.id)}
										>
											Remove
										</button>
									</div>
								))}
								<div className='flex flex-row items-center gap-4 rounded-xl border border-dashed border-deselectPurple px-4 py-3'>
									<select
										className='flex-1 border border-primaryViolet rounded px-3 h-[40px] text-[14px] text-darkenPurple bg-white'
										value={pendingLocale?.id ?? ''}
										onChange={(e) => {
											const found = locales.find((l: any) => l.id === e.target.value);
											setPendingLocale(found ?? null);
											setLocalizedAudioError(null);
										}}
									>
										<option value=''>Select locale</option>
										{locales
											.filter((l: any) => l.code?.toLowerCase() !== 'en')
											.filter((l: any) => !localizedAudios.find((a: any) => a.locale.id === l.id))
											.map((l: any) => (
												<option key={l.id} value={l.id}>
													{l.name} ({l.code})
												</option>
											))}
									</select>
									<button
										type='button'
										disabled={!pendingLocale || localizedAudioUploading}
										className='text-primaryViolet rounded-full bg-lightBG py-2 px-4 font-semibold text-[14px] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primaryViolet hover:text-white transition'
										onClick={() => document.getElementById('localized_audio_upload_modal')?.click()}
									>
										{localizedAudioUploading ? <Loading primaryColor='#5E639B' secondaryColor='#FDDF6D' size={18} stroke={5} /> : 'Upload Audio'}
									</button>
								</div>
								{localizedAudioError && <p className='text-redError text-[13px] font-medium'>{localizedAudioError}</p>}
							</div>
						)}
					</div>
					<input style={{ display: 'none' }} accept='audio/*' type='file' id='localized_audio_upload_modal' onChange={onLocalizedAudioChange} onClick={(e: any) => { e.target.value = null; }} />
					{/* End Localized Audio Section */}

						<div className='mt-2'>
						<ListOfParagraphs paragraphs={paragraphs} setParagraphs={setParagraphs} paragraphTextError={paragraphTextError} />
					</div>
					<div className='flex flex-row justify-end w-full gap-5 mt-5'>
							{/* <div
								className='flex-1 max-w-[160px] flex flex-row items-center justify-center cursor-pointer rounded-full bg-[#fafaff]  h-[60px] button-transparent-class'
								onClick={closeModal}
							>
								<p className='text-[18px] text-darkenPurple leading-5 font-semibold mr-3 '>Back</p>
							</div> */}
							<button className='flex-1 max-w-[160px] flex flex-row items-center justify-center cursor-pointer  rounded-full bg-primaryViolet  min-h-[60px] button-save-class transition' disabled={processing} onClick={() => onSubmit()}>
								{processing ? (
									<Loading primaryColor='#FDDF6D' secondaryColor='#5E639B' size={25} stroke={7} />
								) : (
									<div className='flex flex-row items-center justify-center'>
										<p className='text-[18px]  leading-5 font-semibold mr-3 '>Save</p>
										<Save />
									</div>
								)}
							</button>
						</div>
					</div>
				</div>
			</div>
		</ReactModal>
	);
}

export default CreateEditSolutionModal;
